import { asApiFailure, parseJsonObject } from "@utils/apiFailure";

// What a refused POST organizations (C3, API PR 419) means to the New Client
// drawer. The API's 409 codes, each with its own line, plus "other" for
// everything useOrganizationApi toasted, with one of two deliberate
// fallbacks below.
export interface ExistingClient {
    id: string;
    name: string;
}

export type CreateClientRefusal =
    | { kind: "email_in_use"; message: string; existingOrganizationId?: string }
    | { kind: "email_registered"; message: string }
    // Warn, but allow (PO, C3 follow-up): the drawer names the other clients
    // and offers to create this one anyway.
    | {
        kind: "tax_id_in_use";
        message: string;
        existingOrganizations: ExistingClient[];
    }
    | { kind: "other"; message: string };

// The API answers a 4xx before it writes anything: a validation 400, the
// admin wall's 403.
const NOT_CREATED =
    "The client was not created. Your entries are still here, so you can try again.";

// Anything else - a 5xx, a network failure - may come after the API committed
// the client (it saves the client before approving its location, sending the
// invitation and answering). A retry is safe: the contact email is then
// refused as in use, naming the client (email_in_use).
const MAYBE_CREATED =
    "The client may not have been created. Your entries are still here, so try again: if it was created, you'll be told and can open it.";

// The drawer's own words for a coded 409 whose body carried no message.
const FALLBACK = {
    email_in_use:
        "This email already belongs to another client. Open that client, or use a different contact email.",
    email_registered:
        "This email already has a sign-in, so its owner couldn't set up this client's account. Use a different contact email.",
    tax_id_in_use:
        "Another client already uses this tax ID. Check it isn't the same business, then create the client anyway.",
} as const;

// The listed clients that name both an id and a name; anything else is left
// out rather than shown as a blank link.
const existingClients = (value: unknown): ExistingClient[] =>
    Array.isArray(value)
        ? value.flatMap((entry: unknown) => {
            if (typeof entry !== "object" || entry === null) {
                return [];
            }
            const { id, name } = entry as Record<string, unknown>;
            return typeof id === "string" && typeof name === "string"
                ? [{ id, name }]
                : [];
        })
        : [];

export const createClientRefusal = (error: unknown): CreateClientRefusal => {
    const failure = asApiFailure(error);

    if (failure === undefined || failure.status >= 500) {
        return { kind: "other", message: MAYBE_CREATED };
    }

    if (failure.status !== 409) {
        return { kind: "other", message: NOT_CREATED };
    }

    const body = parseJsonObject(failure.detail);
    const code = body?.error;
    const apiMessage =
        typeof body?.message === "string" ? body.message.trim() : "";

    switch (code) {
        case "email_in_use": {
            const refusal: CreateClientRefusal = {
                kind: "email_in_use",
                message: apiMessage || FALLBACK.email_in_use,
            };
            if (typeof body?.existingOrganizationId === "string") {
                refusal.existingOrganizationId = body.existingOrganizationId;
            }
            return refusal;
        }
        case "email_registered":
            return {
                kind: "email_registered",
                message: apiMessage || FALLBACK.email_registered,
            };
        case "tax_id_in_use":
            return {
                kind: "tax_id_in_use",
                message: apiMessage || FALLBACK.tax_id_in_use,
                existingOrganizations: existingClients(
                    body?.existingOrganizations,
                ),
            };
        default:
            // A 409 the drawer does not know: the toast was suppressed, so
            // the API's sentence (or ours) is the only word on it.
            return { kind: "other", message: apiMessage || NOT_CREATED };
    }
};
